import '../css/index.scss';

const escapeHtml = (value) => String(value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');

const updateTurnstileValidity = (input) => {
    if (!input) return;
    input.setCustomValidity(
        input.value ? '' : (input.dataset.captchaRequiredMessage || 'Please complete the CAPTCHA.'),
    );
};

const updateChannelsValidity = (form) => {
    const channels = Array.from(form.querySelectorAll('input[name="promotion_channels"]'));
    const firstChannel = channels[0];
    if (!firstChannel) return;
    const message = form.dataset.channelsRequiredMessage || 'Select at least one promotion channel.';
    firstChannel.setCustomValidity(
        channels.some((channel) => channel.checked) ? '' : message,
    );
};

window.affiliateTurnstileCompleted = (token) => {
    const input = document.getElementById('id_turnstile_token');
    if (input) {
        input.value = token;
        updateTurnstileValidity(input);
        input.closest('form')?.querySelector('.affiliate-turnstile')?.classList.remove('affiliate-field--error');
    }
};

const closeAffiliateSelect = (select) => {
    select.classList.remove('is-open');
    select.querySelector('.integrations-select__toggle')?.setAttribute('aria-expanded', 'false');
};

const openAffiliateSelect = (select) => {
    document.querySelectorAll('.affiliate-custom-select.is-open').forEach((openSelect) => {
        if (openSelect !== select) closeAffiliateSelect(openSelect);
    });
    select.classList.add('is-open');
    select.querySelector('.integrations-select__toggle')?.setAttribute('aria-expanded', 'true');
};

const updateAffiliateSelect = (select, nativeSelect, value) => {
    nativeSelect.value = value;
    nativeSelect.dispatchEvent(new Event('change', {bubbles: true}));

    const selectedOption = nativeSelect.options[nativeSelect.selectedIndex];
    select.querySelector('.integrations-select__label').textContent = selectedOption?.textContent || '';
    select.querySelectorAll('.integrations-select__option').forEach((option) => {
        const selected = option.dataset.value === value;
        option.classList.toggle('is-selected', selected);
        option.setAttribute('aria-selected', selected ? 'true' : 'false');
    });
};

const enhanceAffiliateSelect = (nativeSelect) => {
    if (nativeSelect.dataset.affiliateSelectEnhanced === 'true') return;

    const field = nativeSelect.closest('.affiliate-field');
    const label = field?.querySelector(`label[for="${nativeSelect.id}"]`);
    const selectedOption = nativeSelect.options[nativeSelect.selectedIndex] || nativeSelect.options[0];
    const customId = `${nativeSelect.id || nativeSelect.name}_custom`;
    const optionsId = `${customId}_options`;
    const optionsMarkup = Array.from(nativeSelect.options).map((option, index) => {
        const selected = option.value === nativeSelect.value;
        const isPlaceholder = option.value === '';
        const isUnavailable = option.disabled || isPlaceholder;
        return `
            <li
                class="integrations-select__option${isPlaceholder ? ' integrations-select__option--placeholder' : ''}${isUnavailable ? ' integrations-select__option--disabled' : ''}${selected ? ' is-selected' : ''}"
                role="option"
                tabindex="-1"
                id="${escapeHtml(optionsId)}_${index}"
                aria-selected="${selected ? 'true' : 'false'}"
                aria-disabled="${isUnavailable ? 'true' : 'false'}"
                data-value="${escapeHtml(option.value)}"
            >${escapeHtml(option.textContent)}</li>
        `;
    }).join('');

    const wrapper = document.createElement('div');
    wrapper.className = 'integrations-select affiliate-custom-select';
    wrapper.id = customId;
    wrapper.innerHTML = `
        <button
            type="button"
            class="integrations-select__toggle"
            aria-haspopup="listbox"
            aria-expanded="false"
            aria-controls="${escapeHtml(optionsId)}"
            aria-label="${escapeHtml(label?.textContent || nativeSelect.name)}"
        >
            <span class="integrations-select__label">${escapeHtml(selectedOption?.textContent || '')}</span>
            <span class="integrations-select__arrow" aria-hidden="true"></span>
        </button>
        <div class="integrations-select__dropdown" role="presentation">
            <ul class="integrations-select__options" role="listbox" id="${escapeHtml(optionsId)}">
                ${optionsMarkup}
            </ul>
        </div>
    `;

    nativeSelect.classList.add('affiliate-native-select');
    nativeSelect.dataset.affiliateSelectEnhanced = 'true';
    nativeSelect.insertAdjacentElement('afterend', wrapper);

    const toggle = wrapper.querySelector('.integrations-select__toggle');
    const options = Array.from(wrapper.querySelectorAll('.integrations-select__option'));
    const navigableOptions = options.filter((option) => option.getAttribute('aria-disabled') !== 'true');
    const selectedOptionIndex = () => {
        const index = navigableOptions.findIndex((option) => option.dataset.value === nativeSelect.value);
        return index >= 0 ? index : 0;
    };
    let activeOptionIndex = selectedOptionIndex();

    const setActiveOption = (index, {focus = false} = {}) => {
        if (!navigableOptions.length) return;
        activeOptionIndex = (index + navigableOptions.length) % navigableOptions.length;
        options.forEach((option) => option.classList.remove('is-active'));
        const activeOption = navigableOptions[activeOptionIndex];
        activeOption.classList.add('is-active');
        wrapper.querySelector('.integrations-select__options')?.setAttribute(
            'aria-activedescendant',
            activeOption.id,
        );
        if (focus) activeOption.focus();
    };

    setActiveOption(activeOptionIndex);

    toggle.addEventListener('click', () => {
        if (wrapper.classList.contains('is-open')) {
            closeAffiliateSelect(wrapper);
        } else {
            setActiveOption(selectedOptionIndex());
            openAffiliateSelect(wrapper);
        }
    });

    navigableOptions.forEach((option) => {
        option.addEventListener('click', () => {
            updateAffiliateSelect(wrapper, nativeSelect, option.dataset.value);
            setActiveOption(navigableOptions.indexOf(option));
            closeAffiliateSelect(wrapper);
            toggle.focus();
        });
    });

    wrapper.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') {
            event.preventDefault();
            closeAffiliateSelect(wrapper);
            toggle.focus();
            return;
        }
        if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            if (!wrapper.classList.contains('is-open')) {
                setActiveOption(selectedOptionIndex());
                openAffiliateSelect(wrapper);
                return;
            }
            const active = navigableOptions[activeOptionIndex];
            if (active) {
                updateAffiliateSelect(wrapper, nativeSelect, active.dataset.value);
                setActiveOption(activeOptionIndex);
                closeAffiliateSelect(wrapper);
                toggle.focus();
            }
            return;
        }
        if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
            event.preventDefault();
            openAffiliateSelect(wrapper);
            const delta = event.key === 'ArrowDown' ? 1 : -1;
            const focusedIndex = navigableOptions.indexOf(document.activeElement);
            const currentIndex = focusedIndex >= 0 ? focusedIndex : activeOptionIndex;
            setActiveOption(currentIndex + delta, {focus: true});
        }
    });
};

const initializeAffiliatePage = () => {
    document.querySelectorAll('[data-affiliate-application-form]').forEach((form) => {
        const channels = Array.from(form.querySelectorAll('input[name="promotion_channels"]'));
        const turnstileInput = form.querySelector('input[name="turnstile_token"]');
        form.dataset.channelsRequiredMessage = form.dataset.channelsRequiredMessage ||
            form.querySelector('[data-affiliate-channels-fieldset]')?.dataset.requiredMessage || '';
        updateChannelsValidity(form);
        if (turnstileInput) {
            turnstileInput.dataset.captchaRequiredMessage = turnstileInput.dataset.captchaRequiredMessage ||
                form.dataset.captchaRequiredMessage || '';
            updateTurnstileValidity(turnstileInput);
        }

        channels.forEach((channel) => channel.addEventListener('change', () => {
            updateChannelsValidity(form);
            form.querySelector('[data-affiliate-channels-fieldset]')?.classList.remove('affiliate-field--error');
        }));
        form.addEventListener('input', (event) => {
            event.target.closest('.affiliate-field, .affiliate-consents')?.classList.remove('affiliate-field--error');
        });
        form.addEventListener('change', (event) => {
            event.target.closest('.affiliate-field, .affiliate-consents')?.classList.remove('affiliate-field--error');
            if (event.target.matches('input[type="checkbox"]')) {
                event.target.closest('.affiliate-consents')?.classList.remove('affiliate-field--error');
            }
        });
        form.addEventListener('invalid', (event) => {
            const field = event.target.closest('.affiliate-field, .affiliate-fieldset, .affiliate-consents');
            if (field) {
                field.classList.add('affiliate-field--error');
            } else if (event.target === turnstileInput) {
                form.querySelector('.affiliate-turnstile')?.classList.add('affiliate-field--error');
            }
        }, true);
        form.addEventListener('submit', (event) => {
            updateChannelsValidity(form);
            updateTurnstileValidity(turnstileInput);
            const hasChannels = channels.some((channel) => channel.checked);
            const hasTurnstile = Boolean(turnstileInput?.value);
            if (!form.checkValidity() || !hasChannels || !hasTurnstile) {
                event.preventDefault();
                form.reportValidity();
                if (!hasTurnstile && hasChannels && form.checkValidity()) {
                    form.querySelector('.affiliate-turnstile')?.scrollIntoView({
                        behavior: 'smooth',
                        block: 'center',
                    });
                }
            }
        });
    });

    document.querySelectorAll('.affiliate-field select').forEach(enhanceAffiliateSelect);

    document.querySelectorAll('img[data-affiliate-fallback]').forEach((image) => {
        const useFallback = () => {
            const fallback = image.dataset.affiliateFallback;
            if (fallback && image.src !== new URL(fallback, window.location.href).href) {
                image.src = fallback;
            }
        };
        image.addEventListener('error', useFallback, {once: true});
        if (image.complete && image.naturalWidth === 0) useFallback();
    });

    document.querySelectorAll('a[href^="#"]').forEach((link) => {
        const targetSelector = link.getAttribute('href');
        if (!targetSelector || targetSelector === '#') return;
        link.addEventListener('click', (event) => {
            const target = document.querySelector(targetSelector);
            if (!target) return;
            event.preventDefault();
            target.scrollIntoView({behavior: 'smooth', block: 'start'});
        });
    });

    document.addEventListener('click', (event) => {
        document.querySelectorAll('.affiliate-custom-select.is-open').forEach((select) => {
            if (!select.contains(event.target)) closeAffiliateSelect(select);
        });
    });
};

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initializeAffiliatePage, {once: true});
} else {
    initializeAffiliatePage();
}
